// Liste Geotab d'une journée passée, la même pour l'épingle du Dispatch, son survol, la fenêtre Geotab et « Clore la
// journée » : les écarts entre le Dispatch et Geotab, en deux sens qui ne se corrigent pas pareil. Le serveur a déjà retiré
// ce qu'une commande explique.

import { fmtDistance, fmtDuration, fmtTime } from "../components/geotab/geotabFormat.js";
import { checkItemText } from "./dispatchGeotabCheck.js";

// Sortes de lignes, de la plus grave à la moins grave, par sens : prévu au Dispatch, pas vu comme prévu par Geotab
// (statut d'une commande, site prévu pas vu) ; vu par Geotab, absent du Dispatch (cas d'un écart).
const PLANNED = ["missing", "other", "check", "unknown", "missed"];
const SEEN = ["unassigned", "pm", "none"];
const KINDS = [...PLANNED, ...SEEN];
const rank = (kind) => (KINDS.includes(kind) ? KINDS.indexOf(kind) : KINDS.length);

export const LIST_SECTIONS = [
  { key: "planned", title: "Prévu au Dispatch, pas vu par Geotab", why: "une commande prévue sans arrêt du bon camion chez le client" },
  { key: "seen", title: "Vu par Geotab, absent du Dispatch", why: "un camion a chargé ou roulé sans que le Dispatch le prévoie" },
];

export const isDoubtful = (order) => order.status !== "found";

// « Carte » d'une commande : arrêt loin de l'adresse à confirmer, ou livraison comparée à une position confirmée.
export const hasPlaceMap = (row) => Boolean(row.order?.point && row.order.stop_point && (row.kind === "check" || row.order.confirmed));

const plural = (count, word) => `${count} ${word}${count > 1 ? "s" : ""}`;

// Les lignes les plus graves d'abord, puis par camion, celles sans camion à la fin de leur sorte.
const byRank = (a, b) => rank(a.kind) - rank(b.kind) || !a.truck - !b.truck || a.truck.localeCompare(b.truck, "fr");

// { planned, seen, found (commandes avec arrêt du bon camion), count (écarts), total (commandes), ordersRead }.
export function dayList(data) {
  if (!data?.eligible) return { planned: [], seen: [], found: [], count: 0, total: 0, ordersRead: false };
  const trucks = new Map((data.trucks || []).map((truck) => [truck.truck_name, truck]));
  const ofOrder = (order) => {
    const truck = order.planned_truck || "";
    return { kind: order.status, truck, truckId: trucks.get(truck)?.truck_id || "", driver: trucks.get(truck)?.driver || "", order };
  };
  const orders = data.orders?.items || [];
  const rows = [
    ...orders.filter(isDoubtful).map(ofOrder),
    ...(data.items || []).map((item) => ({ kind: item.case, truck: item.truck_name, truckId: item.truck_id, driver: item.driver, item })),
    ...(data.missed || []).flatMap((entry) => (entry.unlisted || []).map((place) =>
      ({ kind: "missed", truck: entry.truck_name, truckId: entry.truck_id, driver: entry.driver, place }))),
  ].sort(byRank);
  const planned = rows.filter((row) => PLANNED.includes(row.kind));
  const seen = rows.filter((row) => !PLANNED.includes(row.kind));
  const found = orders.filter((order) => !isDoubtful(order)).map(ofOrder)
    .sort((a, b) => a.truck.localeCompare(b.truck, "fr") || String(a.order.client).localeCompare(String(b.order.client), "fr"));
  return { planned, seen, found, count: rows.length, total: data.orders?.total || 0, ordersRead: Boolean(data.orders) };
}

// « 6 écarts entre le Dispatch et Geotab » ; « 58 commandes sur 62 concordent ».
export const countText = (list) => `${plural(list.count, "écart")} entre le Dispatch et Geotab`;
export const concordText = (list) => (list.ordersRead && list.total
  ? `${plural(list.found.length, "commande")} sur ${list.total} concord${list.found.length > 1 ? "ent" : "e"}` : "");

// Une ligne : le lieu et son repère (numéro de commande ou heures), produit, tonnes, ce qui s'est passé et une piste.
export function rowCells(row, date) {
  if (row.order) {
    const order = row.order;
    const cells = { place: order.client, sub: order.id, product: order.product, tons: order.tons, text: order.detail, hint: "" };
    const at = `à ${fmtTime(order.arrival)}${order.dwell == null ? "" : ` (${fmtDuration(order.dwell)})`}`;
    if (order.status === "missing") {
      return order.planned_truck
        ? { ...cells, text: "Aucun camion ne s’y est arrêté.", hint: "Pas livrée ?" }
        : { ...cells, text: "Commande sur aucun camion ; personne ne s’y est arrêté.", hint: "Reportée ?" };
    }
    if (order.status === "other" && order.seen_truck) {
      return { ...cells, text: `Arrêt de ${order.seen_truck} ${at}${order.planned_truck ? `, pas de ${order.planned_truck}` : ""}.`, hint: "Camion à corriger ?" };
    }
    if (order.status === "check") return { ...cells, text: `Arrêt à ${fmtDistance(order.distance_m)} de l’adresse, ${at}.`, hint: "Lieu à confirmer ?" };
    if (order.status === "unknown") return { ...cells, text: `${order.detail}.`, hint: "Adresse à corriger ?" };
    return order.confirmed ? { ...cells, text: `${order.detail} · position confirmée` } : cells;
  }
  if (row.item) {
    const stops = row.item.stops || [];
    const first = stops[0] || {};
    const last = stops[stops.length - 1] || first;
    const { what, proof } = checkItemText(row.item, date);
    const hours = `${fmtTime(first.arr)} → ${fmtTime(last.dep)}`;
    const hint = { unassigned: "Tournée à inscrire ?", pm: "Chargé PM à inscrire ?", none: proof ? "" : "Commande manquante ?" }[row.kind] || "";
    return { place: first.place, sub: stops.length > 1 ? `→ ${last.place} · ${hours}` : `${hours} · ${fmtDuration(first.dwell)}`,
      product: "", tons: null, text: [what, proof].filter(Boolean).join(" "), hint };
  }
  return { place: row.place, sub: "", product: "", tons: null, text: "Geotab n’a vu aucun arrêt à ce site prévu.", hint: "Pas fait ?" };
}

// Bandeau du lecteur ouvert depuis la liste : les commandes douteuses du camion, par statut puis par client.
export function truckOrderNotes(orders = [], truckName = "") {
  const notes = new Map();
  orders.filter((order) => isDoubtful(order) && order.planned_truck === truckName).forEach((order) => {
    const clients = notes.get(order.status) || new Map();
    clients.set(order.client, [...(clients.get(order.client) || []), order.id]);
    notes.set(order.status, clients);
  });
  return [...notes].sort(([a], [b]) => rank(a) - rank(b)).map(([status, clients]) => ({ status, clients: [...clients] }));
}
